import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
} from "@heroicons/react/20/solid";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
// Hook-based auto-dismissing banner component
import { Message } from "../../hooks/use-message";

interface InlineBannerProps {
    type: "success" | "warning" | "error" | "info";
    showIcon?: boolean;
    fontStyle?: "bold" | "normal" | "semibold" | "medium";
    children: React.ReactNode;
}

const getColorClasses = (
    type: InlineBannerProps["type"],
): { container: string; icon: string; text: string } => {
    switch (type) {
        case "success":
            return {
                container: "bg-green-50",
                icon: "text-green-400",
                text: "text-green-800",
            };
        case "warning":
            return {
                container: "bg-yellow-50",
                icon: "text-yellow-400",
                text: "text-yellow-800",
            };
        case "error":
            return {
                container: "bg-red-50",
                icon: "text-red-400",
                text: "text-red-800",
            };
        case "info":
            return {
                container: "bg-blue-50",
                icon: "text-blue-400",
                text: "text-blue-700",
            };
        default:
            throw new Error(`Unknown type ${type}`);
    }
};

const getIcon = (type: InlineBannerProps["type"], iconClasses: string) => {
    const iconProps = {
        "aria-hidden": true,
        className: `size-5 ${iconClasses}`,
    };

    switch (type) {
        case "success":
            return <CheckCircleIcon {...iconProps} />;
        case "info":
            return <InformationCircleIcon {...iconProps} />;
        case "warning":
        case "error":
            return <ExclamationTriangleIcon {...iconProps} />;
        default:
            return null;
    }
};

export const InlineBanner: React.FC<InlineBannerProps> = props => {
    const { type, children } = props;
    const showIcon = typeof props.showIcon === "boolean" ? props.showIcon : true;
    const fontStyle = props.fontStyle || "normal";

    const colors = getColorClasses(type);
    const fontClass = fontStyle === "medium" ? "font-medium" : `font-${fontStyle}`;

    return (
        <div className={`rounded-md p-4 ${colors.container}`}>
            <div className="flex">
                {showIcon && <div className="shrink-0">{getIcon(type, colors.icon)}</div>}
                <div className={`min-w-0 flex-1 ${showIcon ? "ml-3" : ""}`}>
                    <div className={`text-sm ${fontClass} ${colors.text}`}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface InlineTransientBannerOwnProps {
    timeout?: number;
    children: React.ReactNode;
}

export interface InlineTransientBannerRef {
    show: () => void;
}

const InlineTransientBanner = forwardRef<
    InlineTransientBannerRef,
    InlineTransientBannerOwnProps & InlineBannerProps
>((props, ref) => {
    const [visible, setVisible] = useState(false);
    const { timeout, ...rest } = props;
    const realTimeout = timeout || 3000;
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (visible) {
            timeoutIdRef.current = setTimeout(() => {
                setVisible(false);
            }, realTimeout);
        }
        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [visible, realTimeout]);

    useImperativeHandle(ref, () => ({
        show: () => setVisible(true),
    }));

    return (
        <div
            className={`${
                visible ? "opacity-100" : "opacity-0"
            } transition-opacity ease-in duration-75`}>
            <InlineBanner {...rest} />
        </div>
    );
});

InlineTransientBanner.displayName = "InlineTransientBanner";
export { InlineTransientBanner };

interface MessageBannerProps {
    message: Message | null;
}

export const MessageBanner: React.FC<MessageBannerProps> = ({ message }) => {
    if (!message) {
        return null;
    }

    return (
        <div
            className="transition-all duration-300 ease-in-out animate-in slide-in-from-top-2 fade-in-0"
            key={message.id}>
            <InlineBanner type={message.type} fontStyle="medium">
                {message.content}
            </InlineBanner>
        </div>
    );
};
